import { useState } from 'react';
import axios from 'axios';
import { X, Eye, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/native-datetime';
import { toast } from 'sonner';
import { formatSubmitError } from '@/utils/formErrors';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_NAMES as FALLBACK_NAMES } from '@/lib/workCalculations';
import useDirectory from '@/hooks/useDirectory';
import useUsedDates from '@/hooks/useUsedDates';
import { todayISO, Req, Opt, labelCls, fieldInput, fieldTextarea, fieldTrigger, btnPrimary, btnSecondary } from '@/components/forms/formKit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  form_type: 'leave',
  employee_name: '',
  employee_code: '',
  id_card: '',
  department: '',
  leave_type: '',
  other_label: '欠勤',
  leave_date: todayISO(),
  leave_end_date: '',
  reason: '',
};

export default function LeaveFormModal({ onClose, initialData, submissionId, embedded = false }) {
  const navigate = useNavigate();
  const { employees, employeeNames, departmentNames } = useDirectory();
  const empOptions = employeeNames.length ? employeeNames : FALLBACK_NAMES;
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...EMPTY, ...(initialData || {}) }));
  const isEdit = !!submissionId;
  const usedDates = useUsedDates('leave', [formData.employee_name], submissionId);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-fill employee_code + id_card when employee name is selected
      if (field === 'employee_name') {
        const emp = employees.find((e) => e.name === value);
        updated.employee_code = emp && emp.code ? emp.code : '';
        updated.id_card = emp && emp.id_card ? emp.id_card : '';
        if (emp && emp.department_name && !prev.department) updated.department = emp.department_name;
      }
      // When department changes, clear employee selection if not in this dept
      if (field === 'department') {
        const emp = employees.find((e) => e.name === prev.employee_name);
        if (!emp || emp.department_name !== value) updated.employee_name = '';
      }
      return updated;
    });
  };

  const filteredEmpOptions = formData.department
    ? employees.filter((e) => e.department_name === formData.department).map((e) => e.name)
    : empOptions;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (isEdit) {
        await axios.put(`${API}/forms/submissions/${submissionId}`, { form_data: formData });
        toast.success('申請を更新しました');
        if (onClose) onClose();
        setTimeout(() => navigate(`/submission/${submissionId}`), 300);
      } else {
        await axios.post(`${API}/forms/submit`, formData);
        toast.success('登録しました — 履歴に追加されました');
        // Reset form for next submission instead of navigating away
        setFormData({ ...EMPTY });
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(formatSubmitError(error, isEdit));
    } finally {
      setSubmitting(false);
    }
  };

  const canPreview = formData.employee_name && formData.leave_type && formData.department &&
    formData.leave_date && formData.reason;

  const body = (
    <>
      <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
          休暇届{isEdit && <span className="ml-2 text-sm font-normal text-zinc-500">— 編集</span>}
        </h2>
        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900">
            <X size={24} />
          </button>
        )}
      </div>
      {!showPreview ? (
          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <Label htmlFor="department" className={labelCls}>所属部署<Req /></Label>
              {departmentNames.length > 0 ? (
                <Select value={formData.department} onValueChange={(val) => handleChange('department', val)}>
                  <SelectTrigger data-testid="select-department" className={`mt-1.5 ${fieldTrigger}`}>
                    <SelectValue placeholder="所属部署を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {departmentNames.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={`mt-1.5 ${fieldInput}`}
                  required
                />
              )}
            </div>
            <div>
              <Label className={labelCls}>氏名<Req /></Label>
              <Select
                value={formData.employee_name}
                onValueChange={(val) => handleChange('employee_name', val)}
                disabled={!formData.department && departmentNames.length > 0}
              >
                <SelectTrigger data-testid="select-employee-name" className={`mt-1.5 ${fieldTrigger} disabled:opacity-50`}>
                  <SelectValue placeholder={formData.department ? '氏名を選択してください' : 'まず所属部署を選択してください'} />
                </SelectTrigger>
                <SelectContent>
                  {filteredEmpOptions.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.department && filteredEmpOptions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">この部署に登録された社員がいません。</p>
              )}
            </div>
            <div>
              <Label htmlFor="leave_type" className={labelCls}>休暇種類<Req /></Label>
              <Select value={formData.leave_type} onValueChange={(val) => handleChange('leave_type', val)}>
                <SelectTrigger data-testid="select-leave-type" className={`mt-1.5 ${fieldTrigger}`}>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="有給休暇">有給休暇</SelectItem>
                  <SelectItem value="午前半休">午前半休</SelectItem>
                  <SelectItem value="午後半休">午後半休</SelectItem>
                  <SelectItem value="その他">その他</SelectItem>
                </SelectContent>
              </Select>
              {formData.leave_type === 'その他' && (
                <div className="mt-2">
                  <Label htmlFor="other_label" className="text-xs text-zinc-600">その他 （ ）内の内容</Label>
                  <Input
                    id="other_label"
                    data-testid="input-other-label"
                    value={formData.other_label}
                    maxLength={20}
                    onChange={(e) => handleChange('other_label', e.target.value)}
                    placeholder="例: 欠勤"
                    className="border-zinc-300 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="leave_date" className={labelCls}>取得日<Req /></Label>
                <DatePicker
                  id="leave_date"
                  testId="input-leave-date"
                  value={formData.leave_date}
                  onChange={(v) => handleChange('leave_date', v)}
                  disabledDates={usedDates}
                  disableNonWorking
                  required
                />
              </div>
              <div>
                <Label htmlFor="leave_end_date" className={labelCls}>終了日<Opt /></Label>
                <DatePicker
                  id="leave_end_date"
                  testId="input-leave-end-date"
                  value={formData.leave_end_date}
                  min={formData.leave_date}
                  onChange={(v) => handleChange('leave_end_date', v)}
                  disabledDates={usedDates}
                  disableNonWorking
                />
                <p className="text-xs text-zinc-500 mt-1">複数日の場合のみ入力</p>
              </div>
            </div>
            <div>
              <Label htmlFor="reason" className={labelCls}>事由<Req /></Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                className={`mt-1.5 ${fieldTextarea}`}
                placeholder="例: 私用、通院、家族の介護など"
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose} className={btnSecondary}>
                キャンセル
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                disabled={!canPreview}
                className={btnPrimary}
              >
                <Eye size={18} className="mr-2" />
                プレビュー
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-gradient-to-br from-zinc-50 to-white rounded-lg border-2 border-dashed border-zinc-200 p-6 sm:p-8 mb-6 shadow-inner">
              <h3 className="text-lg font-bold mb-4 border-b border-zinc-300 pb-2">申請内容の確認</h3>
              <dl className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">氏名:</dt>
                  <dd className="col-span-2 text-zinc-900"><span data-no-translate>{formData.employee_name}</span></dd>
                </div>
                {formData.employee_code && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="font-medium text-zinc-600">個人コード:</dt>
                    <dd className="col-span-2 text-zinc-900">{formData.employee_code}</dd>
                  </div>
                )}
                {formData.id_card && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="font-medium text-zinc-600">ID:</dt>
                    <dd className="col-span-2 text-zinc-900 font-mono">{formData.id_card}</dd>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">休暇種類:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.leave_type}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">取得日:</dt>
                  <dd className="col-span-2 text-zinc-900">
                    {formData.leave_date}
                    {formData.leave_end_date && formData.leave_end_date !== formData.leave_date && (
                      <span> 〜 {formData.leave_end_date}</span>
                    )}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">事由:</dt>
                  <dd className="col-span-2 text-zinc-900 whitespace-pre-wrap">{formData.reason}</dd>
                </div>
              </dl>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-zinc-300">
                戻る
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="form-submit-btn" className="bg-teal-700 hover:bg-teal-800 shadow-sm hover:shadow-md transition-all">
                <PaperPlaneTilt size={18} className="mr-2" />
                {submitting ? (isEdit ? '更新中…' : '登録中…') : (isEdit ? '更新する' : '登録する')}
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 w-full overflow-hidden" data-testid="leave-edit">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-900/70 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl border border-zinc-200 w-full max-w-2xl max-h-[calc(100vh-7rem)] overflow-y-auto mt-20 sm:mt-24 mx-4 mb-4">
        {body}
      </div>
    </div>
  );
}
